// @flow

import {toast} from "react-toastify"
import i18n from "i18next"

import {get, post, put, del} from "../client"
import {fetchProducts} from "./products"
import type {
  Baby,
  BabyList,
  BabyListItem,
  Product,
  BabyListOffer,
  ProductSet,
  BabyListOptionalItem,
  Survey,
  ThunkAction,
  Action,
  Dispatch,
  FreeProduct,
} from "../types"

export const fetchBaby = (token: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return get(`/babies/${token}`).then((response) => {
      return dispatch(setBaby(response.data))
    })
  }
}

export const setBaby = (baby: Baby): Action => ({
  type: "SET_BABY",
  baby,
})

export const fetchBabyList = (token: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return get(`/babies/${token}/list`).then((response) => {
      return dispatch(setBabyList(response.data))
    })
  }
}

export const disableWebshopSource = (babyToken: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return post(`/babies/${babyToken}/list/webshop-source/disable`).then(
      (response) => {
        return dispatch(setBabyList(response.data))
      }
    )
  }
}

export const updateFreeProductFromBabyList = (
  babyToken: string,
  babyListItemId: number,
  product: FreeProduct
): ThunkAction => {
  return (dispatch: Dispatch) => {
    return put(
      `/babies/${babyToken}/list/free_product/${babyListItemId}`,
      product
    ).then((response) => {
      dispatch(fetchProducts(babyToken))
      return dispatch(setBabyList(response.data))
    })
  }
}

export const addFreeProductToBabyList = (
  babyToken: string,
  product: FreeProduct
): ThunkAction => {
  return (dispatch: Dispatch) => {
    return post(`/babies/${babyToken}/list/free_product`, product).then(
      (response) => {
        dispatch(fetchProducts(babyToken))
        return dispatch(setBabyList(response.data))
      }
    )
  }
}

export const fetchOffers = (babyToken: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return get(`/babies/${babyToken}/list/offers`).then((response) => {
      dispatch(setOffers(response.data))
    })
  }
}

const setOffers = (offers: BabyListOffer[]): Action => ({
  type: "SET_OFFERS",
  offers,
})

export const sendBabyListOffer = (
  babyToken: string,
  message: string
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(setOfferSending(true))
    return post(`/babies/${babyToken}/list/offers`, {message}).then(
      (response) => {
        dispatch(setOfferSending(false))
        dispatch(setOffers(response.data))
        toast.success(i18n.t("toast.baby.offerSend.success"))
      }
    )
  }
}

export const setOfferSending = (state: boolean): Action => ({
  type: "SET_OFFER_SENDING",
  offerSending: state,
})

export const addProductToBabyList = (
  babyToken: string,
  product: Product | ProductSet,
  productSet: ?ProductSet
): ThunkAction => {
  return (dispatch: Dispatch) => {
    // adding product under set, if divisible this is a noop
    if (productSet && !productSet.isDivisible) {
      return Promise.reject("noop")
    }
    let params = null
    if (productSet) {
      params = {productSetId: productSet.id, productId: product.id}
    } else {
      params = product.isSet
        ? {productSetId: product.id}
        : {productId: product.id}
    }
    return post(`/babies/${babyToken}/list/products`, params).then(
      (response) => {
        return dispatch(setBabyList(response.data))
      }
    )
  }
}

export const removeBabyListItem = (
  babyToken: string,
  babyListItemId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    return del(`/babies/${babyToken}/list/products/${babyListItemId}`).then(
      (response) => {
        return dispatch(setBabyList(response.data))
      }
    )
  }
}

export const updateBabyListItem = (
  babyToken: string,
  babyListItem: BabyListItem
): ThunkAction => {
  return (dispatch: Dispatch) => {
    return put(
      `/babies/${babyToken}/list/products/${babyListItem.id}`,
      babyListItem
    ).then((response) => {
      return dispatch(setBabyList(response.data))
    })
  }
}

export const setBabyList = (babyList: BabyList): Action => ({
  type: "SET_BABY_LIST",
  babyList,
})

export const saveBaby = (token: string, params: Baby): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(startBabySaving())
    return put(`/babies/${token}`, {...params}).then((response) => {
      stopBabySaving(dispatch)
      return dispatch(setBaby(response.data))
    })
  }
}

export const fetchOptionals = (token: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return get(`/babies/${token}/optionals`).then((response) => {
      return dispatch(setOptionals(response.data))
    })
  }
}

export const setOptionals = (
  optionals: $ReadOnlyArray<BabyListOptionalItem>
): Action => ({
  type: "SET_OPTIONALS",
  optionals,
})

export const addOptional = (
  token: string,
  product: Product | ProductSet
): ThunkAction => {
  return (dispatch: Dispatch) => {
    const params = product.isSet
      ? {productSetId: product.id}
      : {productId: product.id}
    return post(`/babies/${token}/optionals`, params).then((response) => {
      return dispatch(setOptionals(response.data))
    })
  }
}

export const removeOptional = (
  token: string,
  optionalId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    return del(`/babies/${token}/optionals/${optionalId}`).then((response) => {
      return dispatch(setOptionals(response.data))
    })
  }
}

export const fetchSurvey = (token: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return get(`/babies/${token}/survey`).then((response) => {
      return dispatch(setSurvey(response.data))
    })
  }
}

export const setSurvey = (survey: Survey): Action => ({
  type: "SET_SURVEY",
  survey,
})

export const updateSurvey = (token: string, survey: Survey): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(startIntakeSaving())
    return put(`/babies/${token}/survey`, {answers: survey}).then(
      (response) => {
        stopIntakeSaving(dispatch)
        return dispatch(setSurvey(response.data))
      }
    )
  }
}

export const startIntakeSaving = (): Action => ({
  type: "START_INTAKE_SAVING",
})

export const stopIntakeSaving = (dispatch: Dispatch) => {
  // fake delay because saving is so fast! The user would not see it
  setTimeout(() => dispatch({type: "STOP_INTAKE_SAVING"}), 500)
}

export const startBabySaving = (): Action => ({
  type: "START_BABY_SAVING",
})

export const stopBabySaving = (dispatch: Dispatch) => {
  // fake delay because saving is so fast! The user would not see it
  setTimeout(() => dispatch({type: "STOP_BABY_SAVING"}), 500)
}
